import { createSelector } from "reselect";
import { getCurrentSale } from "./currentSale";
import { getLabels, getMasterLabels, getSaleLots } from "./root";
import { EMPTY_ARRAY } from "lib";

interface MasterLabelLookup {
  [id: string]: MasterLabel;
}
interface DeploymentLabelLookup {
  [id: string]: DeploymentLabel;
}
interface SaleLotLookup {
  [id: string]: SaleLot;
}

export const getLabelsBySpeciesIdLookup = createSelector(
  [getLabels, getMasterLabels],
  (labels: DeploymentLabelLookup, masterLabels: MasterLabelLookup) =>
    Object.values(labels).reduce((acc, l) => {
      if (l.deleted) {
        return acc;
      }

      const speciesId = l.species_id;
      if (!acc[speciesId]) {
        acc[speciesId] = [];
      }

      acc[speciesId].push({
        ...l,
        masterLabel: masterLabels[l.master_label_id],
      });

      return acc;
    }, {}),
);

export const getLabelsBySpeciesId = speciesId => state =>
  getLabelsBySpeciesIdLookup(state)[speciesId] || EMPTY_ARRAY;

export const getMasterLabelsBySpecies = createSelector(
  [getCurrentSale, getMasterLabels],
  (sale, masterLabels: MasterLabelLookup) =>
    Object.values(masterLabels).filter(ml => ml.speciesId === sale.species_id),
);

export const selectMasterLabelsBySaleLotId = createSelector(
  [getSaleLots, getLabels],
  (saleLots: SaleLotLookup, labels: DeploymentLabelLookup) => {
    const deploymentLabelIdToMasterLabelIdLookup = {};
    Object.values(labels).forEach(l => {
      if (l.master_label_id) {
        deploymentLabelIdToMasterLabelIdLookup[l.id] = l.master_label_id;
      }
    });
    const masterLabelIdsBySaleLotIdLookup = {};
    Object.values(saleLots).forEach(sl => {
      if (!masterLabelIdsBySaleLotIdLookup[sl.id]) {
        masterLabelIdsBySaleLotIdLookup[sl.id] = new Set();
      }
      sl.labels.forEach(l => {
        if (deploymentLabelIdToMasterLabelIdLookup[l]) {
          masterLabelIdsBySaleLotIdLookup[sl.id].add(
            deploymentLabelIdToMasterLabelIdLookup[l],
          );
        }
      });
    });
    return masterLabelIdsBySaleLotIdLookup;
  },
);

export const getLabelById =
  (labelId: string) =>
  (state: State): Label | null =>
    getLabels(state)[labelId] || null;
